// Rich static content for detail pages (frontend content, no backend needed)
import { packages, destinations, featured } from './mock';

export const slugify = (s) =>
  s.toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const GAL = {
  japan: [
    'https://images.unsplash.com/photo-1524413840807-0c3cb6fa808d?w=900&q=80',
    'https://images.unsplash.com/photo-1526481280693-3bfa7568e0f3?w=900&q=80',
    'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=900&q=80',
  ],
  bali: [
    'https://images.unsplash.com/photo-1555400038-63f5ba517a47?w=900&q=80',
    'https://images.unsplash.com/photo-1544644181-1484b3fdfc62?w=900&q=80',
    'https://images.unsplash.com/photo-1518548419970-58e3b4079ab2?w=900&q=80',
  ],
  vietnam: [
    'https://images.unsplash.com/photo-1643029891412-92f9a81a8c16?w=900&q=80',
    'https://images.unsplash.com/photo-1528127269322-539801943592?w=900&q=80',
    'https://images.unsplash.com/photo-1509030450996-dd1a26dda07a?w=900&q=80',
  ],
  thailand: [
    'https://images.unsplash.com/photo-1562602833-0f4ab2fc46e3?w=900&q=80',
    'https://images.unsplash.com/photo-1528181304800-259b08848526?w=900&q=80',
    'https://images.unsplash.com/photo-1519451241324-20b4ea2c4220?w=900&q=80',
  ],
  jordan: [
    'https://images.unsplash.com/photo-1580834341580-8c17a3a630ca?w=900&q=80',
    'https://images.unsplash.com/photo-1554357475-accb8a88a330?w=900&q=80',
    'https://images.unsplash.com/photo-1548786811-dd6e453ccca7?w=900&q=80',
  ],
  kerala: [
    'https://images.unsplash.com/photo-1602216056096-3b40cc0c9944?w=900&q=80',
    'https://images.unsplash.com/photo-1593693411515-c20261bcad6e?w=900&q=80',
    'https://images.unsplash.com/photo-1590050752117-238cb0fb12b1?w=900&q=80',
  ],
};

const galKey = (title) => {
  const t = title.toLowerCase();
  if (t.includes('japan')) return 'japan';
  if (t.includes('bali')) return 'bali';
  if (t.includes('vietnam')) return 'vietnam';
  if (t.includes('thailand')) return 'thailand';
  if (t.includes('jordan')) return 'jordan';
  return 'kerala';
};

const priceNum = (p) => Number(p.replace(/[^0-9]/g, ''));

const itineraries = {
  'japan-discovery': [
    { day: 1, title: 'Arrive in Osaka', desc: 'Warm welcome at Kansai Airport, private transfer to your hotel and an evening stroll through Dotonbori.' },
    { day: 2, title: 'Osaka City & Castle', desc: 'Explore Osaka Castle, Umeda Sky Building and the vibrant Shinsekai district.' },
    { day: 3, title: 'Nara Day Trip', desc: 'Meet the friendly deer of Nara Park and visit the majestic Todai-ji Temple.' },
    { day: 4, title: 'Nagoya & Bullet Train', desc: 'Ride the Shinkansen to Nagoya, tour Nagoya Castle and Osu Shopping Street.' },
    { day: 5, title: 'Hakone & Mt. Fuji Views', desc: 'Scenic ropeway, Lake Ashi cruise and stunning Mt. Fuji panoramas.' },
    { day: 6, title: 'Arrive Tokyo', desc: 'Transfer to Tokyo, evening at Shibuya Crossing and Harajuku.' },
    { day: 7, title: 'Tokyo Highlights', desc: 'Senso-ji Temple, Meiji Shrine, Ginza and a Tokyo Bay cruise.' },
    { day: 8, title: 'Free Day / DisneySea', desc: 'Optional Tokyo DisneySea or shopping in Akihabara.' },
    { day: 9, title: 'Departure', desc: 'Private transfer to the airport with wonderful memories.' },
  ],
};

const genItinerary = (title, places, duration) => {
  const key = slugify(title);
  if (itineraries[key]) return itineraries[key];
  const nights = Number(duration.match(/(\d+)N/)?.[1] || 5);
  const cities = places.split('•').map((c) => c.trim());
  const days = [];
  for (let d = 1; d <= nights + 1; d++) {
    const city = cities[Math.min(cities.length - 1, Math.floor((d - 1) / Math.ceil((nights + 1) / cities.length)))];
    if (d === 1) days.push({ day: d, title: `Arrive in ${cities[0]}`, desc: `Airport pickup, hotel check-in and a relaxed evening to settle in.` });
    else if (d === nights + 1) days.push({ day: d, title: 'Departure', desc: 'Leisure breakfast and private transfer to the airport.' });
    else days.push({ day: d, title: `Discover ${city}`, desc: `Guided sightseeing, iconic landmarks and authentic local experiences in ${city}.` });
  }
  return days;
};

export const packageDetails = packages.map((p) => {
  return {
    ...p,
    slug: slugify(p.title),
    priceValue: priceNum(p.price),
    gallery: p.gallery || GAL[galKey(p.title)],
    overview: p.overview || `${p.title} is a carefully curated ${p.duration} journey through ${p.places}. Enjoy handpicked stays, seamless transfers and unforgettable experiences designed around the way you love to travel.`,
    highlights: p.highlights || [
      `Explore ${p.places}`,
      'Handpicked 4★/5★ accommodation',
      'Private airport transfers & daily breakfast',
      'Expert local guides & curated sightseeing',
      'Best-value pricing with no hidden costs',
    ],
    inclusions: p.inclusions || ['Accommodation on twin-sharing', 'Daily breakfast', 'Airport & inter-city transfers', 'Sightseeing as per itinerary', 'Local support 24/7'],
    exclusions: p.exclusions || ['International flights', 'Visa fees', 'Travel insurance', 'Personal expenses', 'Anything not mentioned in inclusions'],
    itinerary: p.itinerary || genItinerary(p.title, p.places, p.duration),
  };
});

export const getPackage = (slug) => packageDetails.find((p) => p.slug === slug);

// ---- Destinations ----
const featImg = Object.fromEntries(featured.map((f) => [f.name.toLowerCase(), f.img]));

const destMeta = {
  'india': { tagline: 'Timeless heritage, vibrant colours & soulful journeys', highlights: ['Taj Mahal & Golden Triangle', 'Kerala backwaters', 'Rajasthan palaces', 'Himalayan retreats'], tours: [['Golden Triangle', '5N/6D', '₹28,000', featImg['kerala']], ['Kerala Backwaters', '5N/6D', '₹42,000', featImg['kerala']], ['Royal Rajasthan', '6N/7D', '₹49,000', featImg['kerala']]] },
  'south-east-asia': { tagline: 'Tropical islands, ancient temples & buzzing streets', highlights: ['Bali beaches & rice terraces', 'Thailand islands', 'Vietnam’s Ha Long Bay', 'Singapore city breaks'], tours: [['Bali Bliss', '6N/7D', '₹74,500', featImg['bali']], ['Thailand Getaway', '5N/6D', '₹58,000', featImg['thailand']], ['Vietnam Highlights', '7N/8D', '₹89,000', featImg['vietnam']]] },
  'east-asia': { tagline: 'Cherry blossoms, futuristic cities & rich traditions', highlights: ['Japan bullet trains', 'Mt. Fuji views', 'Seoul & K-culture', 'Great Wall of China'], tours: [['Japan Discovery', '8N/9D', '₹1,89,000', featImg['japan']], ['Classic Korea', '5N/6D', '₹92,000', featImg['japan']], ['China Wonders', '7N/8D', '₹1,05,000', featImg['japan']]] },
  'south-asia': { tagline: 'Serene backwaters, spice trails & mountain escapes', highlights: ['Sri Lanka beaches', 'Bhutan monasteries', 'Nepal Himalayas', 'Maldives resorts'], tours: [['Sri Lanka Explorer', '6N/7D', '₹64,000', featImg['kerala']], ['Bhutan Bliss', '5N/6D', '₹78,000', featImg['kerala']], ['Maldives Escape', '3N/4D', '₹85,000', featImg['kerala']]] },
  'middle-east': { tagline: 'Golden deserts, futuristic skylines & ancient wonders', highlights: ['Dubai skyline & desert safari', 'Petra, Jordan', 'Abu Dhabi grand mosque', 'Oman fjords'], tours: [['Dubai Delight', '4N/5D', '₹62,000', featImg['jordan']], ['Jordan Wonders', '6N/7D', '₹1,25,000', featImg['jordan']], ['Oman Explorer', '5N/6D', '₹88,000', featImg['jordan']]] },
  'central-asia': { tagline: 'Silk Road cities, epic landscapes & warm hospitality', highlights: ['Samarkand & Bukhara', 'Kazakh steppes', 'Kyrgyz lakes', 'Silk Road heritage'], tours: [['Uzbekistan Silk Road', '6N/7D', '₹98,000', featImg['jordan']], ['Kazakhstan Escape', '5N/6D', '₹92,000', featImg['jordan']]] },
  'caucasus': { tagline: 'Dramatic mountains, wine valleys & old-world charm', highlights: ['Georgia wine country', 'Armenian monasteries', 'Baku old city', 'Caucasus peaks'], tours: [['Georgia Getaway', '5N/6D', '₹79,000', featImg['jordan']], ['Azerbaijan Explorer', '4N/5D', '₹72,000', featImg['jordan']]] },
  'europe': { tagline: 'Iconic cities, alpine beauty & Mediterranean coasts', highlights: ['Paris & the Eiffel Tower', 'Swiss Alps', 'Santorini sunsets', 'Italian art cities'], tours: [['Best of Europe', '9N/10D', '₹2,15,000', featImg['japan']], ['Greece Island Hopping', '6N/7D', '₹1,35,000', featImg['japan']], ['Swiss & French Alps', '7N/8D', '₹1,78,000', featImg['japan']]] },
  'africa': { tagline: 'Epic safaris, wild landscapes & vibrant cultures', highlights: ['Serengeti & Masai Mara', 'Cape Town', 'Victoria Falls', 'Sahara adventures'], tours: [['Kenya Safari', '6N/7D', '₹1,45,000', featImg['jordan']], ['South Africa Explorer', '7N/8D', '₹1,68,000', featImg['jordan']]] },
  'oceania': { tagline: 'Sun-kissed coasts, reefs & cosmopolitan cities', highlights: ['Sydney Opera House', 'Great Barrier Reef', 'New Zealand fjords', 'Gold Coast beaches'], tours: [['Australia Highlights', '7N/8D', '₹2,25,000', featImg['japan']], ['New Zealand Escape', '8N/9D', '₹2,48,000', featImg['japan']]] },
  'the-americas': { tagline: 'From vibrant cities to breathtaking natural wonders', highlights: ['New York City', 'Machu Picchu', 'Grand Canyon', 'Brazil beaches'], tours: [['USA East Coast', '7N/8D', '₹2,35,000', featImg['japan']], ['Peru & Machu Picchu', '6N/7D', '₹1,95,000', featImg['japan']]] },
  'caribbean': { tagline: 'Turquoise waters, white sands & island rhythm', highlights: ['Bahamas beaches', 'Jamaica vibes', 'Cuba heritage', 'Island cruises'], tours: [['Bahamas Escape', '4N/5D', '₹1,55,000', featImg['bali']], ['Caribbean Cruise', '6N/7D', '₹1,85,000', featImg['bali']]] },
};

export const destinationDetails = destinations.map((d) => {
  const slug = slugify(d.name);
  const meta = destMeta[slug] || { tagline: 'Discover unforgettable experiences', highlights: [], tours: [] };
  return {
    name: d.name,
    slug,
    img: d.img,
    tagline: meta.tagline,
    description: `Discover ${d.name} with Travel Era Holiday. From iconic landmarks to hidden gems, our handcrafted journeys are designed around your interests, pace and travel style — with seamless planning and 24/7 on-trip support.`,
    highlights: meta.highlights,
    bestTime: 'October to March',
    tours: meta.tours.map((t) => ({ name: t[0], duration: t[1], price: t[2], img: t[3] })),
  };
});

export const getDestination = (slug) => destinationDetails.find((d) => d.slug === slug);

// ---- Estimator config ----
export const estimatorRegions = [
  { name: 'India', perNight: 4500 },
  { name: 'South East Asia', perNight: 8000 },
  { name: 'East Asia', perNight: 14000 },
  { name: 'South Asia', perNight: 9000 },
  { name: 'Middle East', perNight: 11000 },
  { name: 'Europe', perNight: 18000 },
  { name: 'Africa', perNight: 16000 },
  { name: 'Oceania', perNight: 20000 },
  { name: 'The Americas', perNight: 19000 },
  { name: 'Caribbean', perNight: 17000 },
];

export const estimatorStyles = [
  { name: 'Budget', mult: 0.8 },
  { name: 'Comfort', mult: 1 },
  { name: 'Luxury', mult: 1.7 },
];

export const estimatorAddons = [
  { key: 'flights', label: 'International Flights', cost: 35000 },
  { key: 'visa', label: 'Visa Assistance', cost: 6000 },
  { key: 'insurance', label: 'Travel Insurance', cost: 2500 },
  { key: 'transfers', label: 'Private Transfers', cost: 4000 },
];
